import axios from 'axios';

// Use relative path for API in production (Cloudflare Pages/Functions)
// This will automatically use the same domain as the frontend
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors and token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      // Try to refresh token before giving up
      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken) {
        try {
          const response = await api.post('/auth/refresh', { refresh_token: refreshToken });
          const { access_token } = response.data;
          
          localStorage.setItem('access_token', access_token);
          api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
          
          // Retry the original request
          const originalRequest = error.config;
          originalRequest.headers.Authorization = `Bearer ${access_token}`;
          return api.request(originalRequest);
        } catch (refreshError) {
          // Refresh failed - clear tokens and redirect
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          delete api.defaults.headers.common['Authorization'];
          window.location.href = '/login';
        }
      } else {
        // No refresh token - redirect to login
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        delete api.defaults.headers.common['Authorization'];
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export default api;

// API service functions
export const authService = {
  // Login with email/password
  login: (email: string, password: string) =>
    api.post('/auth/login', { email, password }),
  
  // Register new user
  register: (email: string, password: string, username?: string, full_name?: string) =>
    api.post('/auth/register', { email, password, username, full_name }),
  
  // Get current user info
  me: () => api.get('/auth/me'),
  
  // Magic link authentication
  requestMagicLink: (email: string) =>
    api.post('/auth/magic-link', { email }),
  
  verifyMagicLink: (token: string) =>
    api.get(`/auth/verify-magic-link?token=${token}`),
  
  // Password reset
  requestPasswordReset: (email: string) =>
    api.post('/auth/reset-password', { email }),
  
  resetPassword: (token: string, password: string) =>
    api.put('/auth/reset-password', { token, password }),
  
  // Token management
  refreshToken: (refresh_token: string) =>
    api.post('/auth/refresh', { refresh_token }),
  
  // Logout
  logout: (refresh_token?: string, all_sessions?: boolean) =>
    api.post('/auth/logout', { refresh_token, all_sessions }),

  // OAuth methods
  initiateOAuth: (provider: string) => {
    // This will redirect to the OAuth provider
    window.location.href = `/api/auth/oauth/${provider}`;
  },

  completeOAuth: (login_token: string) =>
    api.post('/auth/oauth-complete', { login_token }),
};

export const projectService = {
  list: () => api.get('/projects/'),
  
  get: (id: number) => api.get(`/projects/${id}`),
  
  create: (data: any) => api.post('/projects/', data),
  
  update: (id: number, data: any) => api.patch(`/projects/${id}`, data),
  
  delete: (id: number) => api.delete(`/projects/${id}`),
  
  toggleActive: (id: number) => api.post(`/projects/${id}/toggle-active`),
};

export const reelService = {
  list: (params?: any) => api.get('/reels/', { params }),
  
  get: (id: number) => api.get(`/reels/${id}`),
  
  create: (data: any) => api.post('/reels/', data),
  
  update: (id: number, data: any) => api.patch(`/reels/${id}`, data),
  
  delete: (id: number) => api.delete(`/reels/${id}`),
  
  generateScript: (data: any) => api.post('/reels/generate-script', data),
  
  regenerateScript: (id: number, temperature?: number) =>
    api.post(`/reels/${id}/regenerate-script`, { temperature }),
  
  previewVoice: (id: number, voice: string, pitch: number, speed: number) =>
    api.post(`/reels/${id}/preview-voice`, { voice, pitch, speed }),
  
  process: (id: number) => api.post(`/reels/${id}/process`),
  
  batchCreate: (data: any) => api.post('/reels/batch', data),
  
  download: (id: number) => api.get(`/reels/${id}/download`),
};

export const voiceService = {
  list: (language: string) => api.get('/voices/', { params: { language } }),
  
  preview: (data: any) => api.post('/voices/preview', data),
  
  recommended: (language: string, style: string) =>
    api.get('/voices/recommended', { params: { language, style } }),
};

export const publishingService = {
  publish: (data: any) => api.post('/publishing/publish', data),
  
  republish: (resultId: number) => api.post(`/publishing/republish/${resultId}`),
  
  results: (reelId: number) => api.get(`/publishing/results/${reelId}`),
  
  schedule: (reelId: number, data: any) => api.post(`/publishing/schedule/${reelId}`, data),
  
  cancelSchedule: (reelId: number) => api.delete(`/publishing/cancel-schedule/${reelId}`),
  
  queue: (projectId?: number) => api.get('/publishing/queue', { params: { project_id: projectId } }),
};

export const statsService = {
  dashboard: (days?: number) => api.get('/stats/dashboard', { params: { days } }),
  
  project: (projectId: number, days?: number) =>
    api.get(`/stats/project/${projectId}`, { params: { days } }),
  
  trends: (params?: any) => api.get('/stats/trends', { params }),
  
  bestPerforming: (metric: string, limit?: number, projectId?: number) =>
    api.get('/stats/performance/best', { params: { metric, limit, project_id: projectId } }),
};

export const scheduleService = {
  create: (data: any) => api.post('/schedules/', data),
  
  listByProject: (projectId: number) => api.get(`/schedules/project/${projectId}`),
  
  get: (id: number) => api.get(`/schedules/${id}`),
  
  update: (id: number, data: any) => api.patch(`/schedules/${id}`, data),
  
  toggle: (id: number) => api.post(`/schedules/${id}/toggle`),
  
  delete: (id: number) => api.delete(`/schedules/${id}`),
  
  upcoming: (days: number, projectId?: number) =>
    api.get(`/schedules/upcoming/${days}`, { params: { project_id: projectId } }),
};

export const settingsService = {
  get: () => api.get('/settings/'),

  update: (data: any) => api.put('/settings/', data),
};

export const socialService = {
  connect: (platform: string) => {
    window.location.href = `/api/social/connect?platform=${platform}`;
  },

  accounts: () => api.get('/social/accounts'),

  disconnect: (id: string) => api.delete(`/social/accounts?id=${id}`),
};

export const musicService = {
  list: (params?: { genre?: string; mood?: string; duration_min?: number; duration_max?: number }) =>
    api.get('/music/', { params }),

  recommend: (genre: string) =>
    api.post('/music/', { genre }),
};

export const templateService = {
  list: () => api.get('/templates'),

  get: (slug: string) => api.get(`/templates/${slug}`),
};